/**
 * When somebody is free, how they want to train, and what they are training
 * for.
 *
 * Answered once for the person, and again for a sport that wants something
 * different. The API resolves the fallback and says where each value came
 * from, so nothing here recomputes that rule: two implementations of one rule
 * is how two screens end up disagreeing.
 */

export type ActivityMoment = 'morning' | 'midday' | 'evening';
export const ACTIVITY_MOMENTS: readonly ActivityMoment[] = ['morning', 'midday', 'evening'];

/** Monday is 1 and Sunday is 7, as ISO-8601 counts them. */
export type ActivityDay = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const ACTIVITY_DAYS: readonly ActivityDay[] = [1, 2, 3, 4, 5, 6, 7];

export interface ActivitySlot {
  day: ActivityDay;
  moment: ActivityMoment;
}

export type ActivityIntention = 'performance' | 'chill' | 'both';
export const ACTIVITY_INTENTIONS: readonly ActivityIntention[] = ['performance', 'chill', 'both'];

export type SportGoalType = 'race' | 'challenge' | 'weight' | 'pleasure' | 'none';
export const SPORT_GOAL_TYPES: readonly SportGoalType[] = [
  'race',
  'challenge',
  'weight',
  'pleasure',
  'none',
];

/** The goals that happen on a day, and therefore carry one. */
export const GOAL_TYPES_WITH_DATE: readonly SportGoalType[] = ['race', 'challenge', 'weight'];

export interface SportGoal {
  type: SportGoalType;
  name?: string;
  date?: string;
}

/** Where a value on screen came from, which is what the pill says. */
export type PreferenceOrigin = 'general' | 'sport' | 'unset';

export interface GeneralActivityPreferences {
  availability?: ActivitySlot[];
  intention?: ActivityIntention;
}

/** One sport's line on screen, already resolved by the API. */
export interface SportActivityPreferencesView {
  sportId: string;
  availability: ActivitySlot[];
  availabilityFrom: PreferenceOrigin;
  intention?: ActivityIntention;
  intentionFrom: PreferenceOrigin;
  goal?: SportGoal;
}

export interface MyActivityPreferences {
  general: GeneralActivityPreferences;
  sports: SportActivityPreferencesView[];
}

/** True when the slot is already chosen, so the grid can draw its tick. */
export const isSlotChosen = (slots: ActivitySlot[] | undefined, day: ActivityDay, moment: ActivityMoment): boolean =>
  (slots ?? []).some((slot) => slot.day === day && slot.moment === moment);

/**
 * The list with that cell turned on, or off.
 *
 * Returns a new list rather than changing the one it is given: the grid draws
 * from what the state holds, and mutating it in place leaves the screen
 * showing a value nobody saved.
 */
export function toggleSlot(
  slots: ActivitySlot[] | undefined,
  day: ActivityDay,
  moment: ActivityMoment,
): ActivitySlot[] {
  const current = slots ?? [];
  if (isSlotChosen(current, day, moment)) {
    return current.filter((slot) => !(slot.day === day && slot.moment === moment));
  }

  return [...current, { day, moment }].sort(
    (a, b) => a.day - b.day || ACTIVITY_MOMENTS.indexOf(a.moment) - ACTIVITY_MOMENTS.indexOf(b.moment),
  );
}

/** True when a goal of this type needs a date beside it. */
export const goalNeedsDate = (type: SportGoalType | undefined): boolean =>
  !!type && GOAL_TYPES_WITH_DATE.includes(type);
